import React, { useEffect, useRef } from "react";
import styles from "./nyx.module.scss";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";

const SCRIPT_SRC = "https://embed-tickets.nyxapp.net/sdk.js";

let scriptPromise: Promise<void> | null = null;

type NyxMountCall = {
    targetId: string;
    options: Record<string, unknown>;
};

type NyxGlobal = {
    mount: (targetId: string, options: Record<string, unknown>) => void;
};

function isNyxScript(embedCode: string): boolean {
    return embedCode.includes(SCRIPT_SRC);
}

function extractNyxWidget(embedCode: string): string {
    return embedCode.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "").trim();
}

function extractInlineScripts(embedCode: string): string[] {
    const scripts: string[] = [];
    const regex = /<script\b(?![^>]*\bsrc=)[^>]*>([\s\S]*?)<\/script>/gi;
    let match: RegExpExecArray | null = regex.exec(embedCode);

    while (match !== null) {
        const scriptBody = match[1].trim();
        if (scriptBody) {
            scripts.push(scriptBody);
        }
        match = regex.exec(embedCode);
    }

    return scripts;
}

function toJsonLikeObject(objectLiteral: string): string {
    return objectLiteral
        .replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, "$1\"$2\":")
        .replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, '"$1"')
        .replace(/,\s*([}\]])/g, "$1");
}

function parseObjectLiteral(objectLiteral: string): Record<string, unknown> | null {
    try {
        const json = toJsonLikeObject(objectLiteral);
        const parsed = JSON.parse(json) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            return parsed as Record<string, unknown>;
        }
    } catch {
        return null;
    }

    return null;
}

function parseNyxMountCalls(inlineScripts: string[]): NyxMountCall[] {
    const calls: NyxMountCall[] = [];
    const mountRegex = /nyx\.mount\(\s*['\"]([^'\"]+)['\"]\s*,\s*(\{[\s\S]*?\})\s*\)/g;

    inlineScripts.forEach((script) => {
        let match: RegExpExecArray | null = mountRegex.exec(script);

        while (match !== null) {
            const targetId = match[1]?.trim();
            const options = parseObjectLiteral(match[2]);

            if (targetId && options) {
                calls.push({ targetId, options });
            }

            match = mountRegex.exec(script);
        }

        mountRegex.lastIndex = 0;
    });

    return calls;
}

function loadScriptOnce(src: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
        if (existing) {
            resolve();
            return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`${src} failed to load`));

        document.body.appendChild(script);
    });
}

function loadNyxScriptOnce(): Promise<void> {
    if (scriptPromise) {
        return scriptPromise;
    }

    scriptPromise = loadScriptOnce(SCRIPT_SRC).catch((error) => {
        scriptPromise = null;
        throw error;
    });

    return scriptPromise;
}

const nyxEmbed = ({ content, settings }: any) => {
    const wrapperClasses = getWrapperClasses(settings);
    const wrapperStyles = useWrapperStyles(settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground({ content, settings });

    const hostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!content?.embedCode || !hostRef.current) return;

        const embedCode = content.embedCode;
        const inlineScripts = extractInlineScripts(embedCode);
        const mountCalls = parseNyxMountCalls(inlineScripts);

        if (!isNyxScript(embedCode)) return;

        const widgetHtml = extractNyxWidget(embedCode);
        hostRef.current.innerHTML = widgetHtml;

        loadNyxScriptOnce()
            .then(() => {
                const nyx = (window as Window & { nyx?: NyxGlobal }).nyx;
                if (!nyx?.mount) {
                    throw new Error("NYX SDK loaded but nyx.mount is unavailable");
                }

                mountCalls.forEach(({ targetId, options }) => {
                    nyx.mount(targetId, options);
                });
            })
            .catch((error) => {
                console.error("Failed to initialize NYX embed", error);
            });

        return () => {
            if (hostRef.current) {
                hostRef.current.innerHTML = "";
            }
        };
    }, [content?.embedCode]);

    return (
        <section
            className={combineClasses(styles.wrapper, inViewClasses, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles }}
            ref={inViewElementRef}
        >
            <div className={combineClasses(styles.container, ...splitClasses)}>
                <div className={styles.content}>
                    <div ref={hostRef} />
                </div>
            </div>

            {settings.backgroundMedia?.[0] && <BackgroundMedia {...settings.backgroundMedia[0]} />}
        </section>
    );
};

export default nyxEmbed;
